<template>
  <div>
    <section
      id="heroBanners"
      class="d-print-none"
      :aria-roledescription="banners.length > 1 ? 'carousel' : null"
    >
      <div
        v-if="banners.length > 0"
        ref="carousel"
        :key="banners.length"
        aria-live="off"
        class="fipa-carousel ma-auto"
        :style="styleImage"
      >
        <div
          v-for="(b, i) in banners"
          :key="i"
          :class="[
            i == visible
              ? 'fipa-carousel-content__active-slider'
              : 'fipa-carousel-content__inactive-slider'
          ]"
          :data-banner-index="i"
          class="fipa-carousel-content"
          :aria-roledescription="banners.length > 1 ? 'slide' : null"
        >
          <div
            v-if="b.contentType == 'EXTERNAL_ADNUNTIUS'"
            :class="'adn-' + b.contentText.split('_')[1]"
          />
          <div
            v-else-if="b.contentType == 'EXTERNAL_NIF'"
            :id="getNIFMeta(b).placementId"
            style="display: none"
          />
          <component
            :is="b.targetUrl ? 'a' : 'span'"
            v-else
            :href="b.targetUrl ? b.targetUrl : null"
            :target="b.targetUrl ? '_blank' : null"
            class="fipa-carousel-image-container"
            @click="userClickedBanner(b)"
          >
            <picture>
              <source :srcset="b.imageUrl" />
              <img
                :src="b.imageUrl"
                :alt="b.title"
                :style="styleImage"
                class="ma-auto hero-banner"
                style="pointer-events: none"
              />
            </picture>
          </component>
        </div>

        <div v-if="banners.length > 1" class="fipa-carousel-control">
          <div
            v-for="(length, index) in banners.length"
            :key="`control-${length}`"
            aria-controls="heroBanners"
            class="fipa-carousel-control-radio-btn"
            @click="nextSlide(index)"
          >
            <span :class="visible == index ? 'primary' : ''" />
          </div>
        </div>
      </div>
      <div
        v-show="!banners.length"
        class="front-page-main-banner hidden-sm-and-down"
        role="header"
        :style="styleImage"
      >
        <picture
          v-if="
            !$apollo.queries.bannersActive.loading &&
            !banners.length &&
            $federation.heroBanner
          "
        >
          <source
            v-show="!webpDisabled"
            :srcset="$federation.heroBanner.webp"
            type="image/webp"
          />
          <source :srcset="$federation.heroBanner.jpeg" type="image/jpeg" />

          <img
            :src="$federation.heroBanner.jpeg"
            alt="Header banner"
            :style="styleImage"
          />
        </picture>
      </div>
    </section>
  </div>
</template>
<script>
import gql from "fraql";
import { sortBy } from "lodash-es";
import { banners } from "./bannersActive.js";
import { useAdnuntius } from "./adnuntius";
import { useNIF } from "./nif";

export default {
  name: "AppHeroBanner",
  setup() {
    const { adnuntiusFetch } = useAdnuntius();
    const { nifFetch, getNIFMeta } = useNIF();
    return { adnuntiusFetch, nifFetch, getNIFMeta };
  },
  data() {
    return {
      visible: 0,
      nextSlideTimeout: null,
      slideInAction: false,
      observer: null,
      bannersActive: [],
      webpDisabled: false,
      country: null,
      maxWidth: "1480px", // Used in adnuntius
      maxHeight: "370px"
    };
  },
  apollo: {
    bannersActive: {
      query: gql`
        query heroBanners {
          bannersActive {
           ${banners}
          }
        }
      `,
      result({ loading }) {
        if (loading) return;
        this.nifFetch(this.banners);
        this.adnuntiusFetch(this.banners, {
          maxHeight: this.maxHeight,
          maxWidth: this.maxWidth
        });
        this.$nextTick(() => {
          this.prepareCarousel(this.banners);
        });
      },
      fetchPolicy: "cache-first"
    }
  },
  computed: {
    banners() {
      const externalContent = ["EXTERNAL_ADNUNTIUS", "EXTERNAL_NIF"];
      const type = this.$vuetify.breakpoint.lgAndUp
        ? "HERO_DESKTOP"
        : "HERO_MOBILE";
      return sortBy(
        this.bannersActive.filter(
          b =>
            b.placement.includes(type) &&
            (b.imageUrl || externalContent.includes(b.contentType))
        ),
        "rank"
      );
    },
    carouselBannersLength() {
      return this.banners.length;
    },
    styleImage() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return "max-width:3840px;height:370px;";
      } else {
        return "width: 100%;max-width:375px;height:375px;";
      }
    }
  },
  created() {
    if (document.querySelector(".no-webp")) {
      this.webpDisabled = true;
    }
  },
  beforeDestroy() {
    clearTimeout(this.nextSlideTimeout);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    async prepareCarousel(banners) {
      this.visible = Math.floor(Math.random() * banners.length);
      if (banners.length > 1) {
        this.setIntervalCarousel(banners[this.visible].rotateAfterSeconds);
      }
      if (banners.length > 0) {
        this._setupBanners();
      }
    },
    _setupBanners() {
      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            this.observer.unobserve(entry.target);
            if (
              process.env.VUE_APP_GOOGLE_ANALYTICS_TRACKING_ID &&
              entry.target.closest("[data-banner-index]").dataset
            ) {
              const b =
                this.banners[
                  entry.target.closest("[data-banner-index]").dataset
                    .bannerIndex
                ];
              if (this.$gtag) {
                this.$gtag.event("banner_view", {
                  placement: b.placement,
                  position: b.position,
                  title: b.title,
                  customer: b.customerName,
                  content_type: b.contentType
                });
              }
            }
          }
        },
        {
          threshold: 0.35
        }
      );

      Array.from(this.$el.querySelectorAll("img.hero-banner")).forEach(el => {
        function unify(e) {
          return e.changedTouches ? e.changedTouches[0] : e;
        }
        const showPrewiewSlides = () => {
          let nextIndex =
            this.banners.length - 1 >= this.visible + 1 ? this.visible + 1 : 0;
          this.$el
            .querySelector(`[data-banner-index="${nextIndex}"]`)
            .classList.add("fipa-carousel-content__touch_sliding_preview");

          let prevIndex = this.visible - 1 <= 0 ? 0 : this.visible - 1;
          this.$el
            .querySelector(`[data-banner-index="${prevIndex}"]`)
            .classList.add("fipa-carousel-content__touch_sliding_preview");
        };
        this.observer.observe(el);
        const imageContainer = el.closest(".fipa-carousel-image-container");
        const contentRoot = el.closest(".fipa-carousel-content");

        if (
          imageContainer &&
          imageContainer.dataset &&
          !imageContainer.dataset.listenerAttached
        ) {
          let x0 = 0;
          imageContainer.dataset.listenerAttached = true;
          // Made with this guide https://css-tricks.com/simple-swipe-with-vanilla-javascript/
          imageContainer.addEventListener(
            "touchstart",
            evt => {
              clearTimeout(this.nextSlideTimeout);
              this.slideInAction = false;
              // Set original X position
              x0 = unify(evt).clientX;
              contentRoot.classList.add("fipa-carousel-content__touch_sliding");
            },
            { passive: true }
          );
          imageContainer.addEventListener(
            "touchmove",
            evt => {
              // Get new X position and compute where the user moved the slide
              let x1 = unify(evt).clientX;
              el.style.setProperty("--i", `${x1 - x0}px`);
              showPrewiewSlides();
            },
            { passive: true }
          );
          imageContainer.addEventListener(
            "touchend",
            evt => {
              // Get new X position
              let x1 = unify(evt).clientX;
              // Calculate to check if theshrold is over 20% to allow sliding next/prev
              let dx = x1 - x0,
                s = Math.sign(dx),
                f = +((s * dx) / window.innerWidth).toFixed(2);

              if (f > 0.2) {
                // Show the next or previous slide, depending where user moved the slide
                if (x1 <= x0) {
                  this.visible =
                    this.visible + 1 > this.banners.length - 1
                      ? 0
                      : this.visible + 1;
                } else {
                  this.visible =
                    this.visible - 1 >= 0
                      ? this.visible - 1
                      : this.banners.length - 1;
                }
                // Remove previws and restart interval
                this._removePreviews();
                this.setIntervalCarousel(
                  this.banners[this.visible].rotateAfterSeconds
                );
              } else {
                // Otherwiser restart interval
                this._removePreviews();
                this.setIntervalCarousel(
                  this.banners[this.visible].rotateAfterSeconds
                );
              }
            },
            { passive: true }
          );
        }
      });
    },
    _removePreviews() {
      Array.from(this.$el.querySelectorAll("[data-banner-index]")).forEach(
        el => {
          el.classList.remove("fipa-carousel-content__touch_sliding");
          el.classList.remove("fipa-carousel-content__touch_sliding_preview");
          el.querySelector("img").style.setProperty("--i", `0px`);
        }
      );
    },
    userClickedBanner(b) {
      if (process.env.VUE_APP_GOOGLE_ANALYTICS_TRACKING_ID) {
        this.$gtag.event("banner_click", {
          placement: b.placement,
          position: b.position,
          title: b.title,
          customer: b.customerName,
          content_type: b.contentType
        });
      }
    },
    setIntervalCarousel(bannerRotateSeconds) {
      if (!bannerRotateSeconds) return;
      if (this.banners.length <= 1) return;
      this.nextSlideTimeout = setTimeout(() => {
        this.nextSlide();
      }, bannerRotateSeconds * 1000);
      // Wants in milliseconds
    },
    nextSlide(selectedSlideIndex) {
      if (
        this.slideInAction ||
        (selectedSlideIndex && this.visible == selectedSlideIndex)
      ) {
        return false;
      }
      clearTimeout(this.nextSlideTimeout);
      this.slideInAction = true;

      if (selectedSlideIndex) {
        const nextSlide = this.$el.querySelector(
          `.fipa-carousel div.fipa-carousel-content[data-banner-index="${selectedSlideIndex}"]`
        );
        const directionSwitch =
          this.visible > selectedSlideIndex ? "right" : "left";
        if (nextSlide) {
          nextSlide.addEventListener(
            "animationend",
            () => {
              this.visible = selectedSlideIndex;

              nextSlide.classList.remove(
                `fipa-carousel-content__slide-in-${directionSwitch}`
              );
              this.setIntervalCarousel(
                this.banners[selectedSlideIndex].rotateAfterSeconds
              );
              this.slideInAction = false;
            },
            { once: true, passive: true }
          );

          nextSlide.classList.add(
            `fipa-carousel-content__slide-in-${directionSwitch}`
          );
        }
      } else {
        let nextSlideIndex;
        if (selectedSlideIndex == 0) {
          nextSlideIndex = selectedSlideIndex;
        } else {
          nextSlideIndex =
            this.carouselBannersLength > this.visible + 1
              ? this.visible + 1
              : 0;
        }

        const nextSlide = this.$el.querySelector(
          `.fipa-carousel div.fipa-carousel-content[data-banner-index="${nextSlideIndex}"]`
        );
        if (!nextSlide) return false;
        nextSlide.addEventListener(
          "animationend",
          () => {
            this.visible = nextSlideIndex;
            nextSlide.classList.remove("fipa-carousel-content__slide-in-right");
            this.setIntervalCarousel(
              this.banners[nextSlideIndex].rotateAfterSeconds
            );
            this.slideInAction = false;
          },
          { once: true, passive: true }
        );

        nextSlide.classList.add("fipa-carousel-content__slide-in-right");
      }
    }
  }
};
</script>
<style scoped lang="scss">
@keyframes slide-in-left {
  0% {
    transform: translateX(-1vw);
    opacity: 0.65;
  }
  100% {
    transform: translateX(0px);
    opacity: 1;
  }
}

@keyframes slide-in-right {
  0% {
    transform: translateX(1vw);
    opacity: 0.65;
  }
  100% {
    transform: translateX(0px);
    opacity: 1;
  }
}

.fipa-carousel {
  position: relative;
  margin: 1px 0;

  &-content {
    will-change: transformX opacity;
    transition: 0.3s ease 0.1s;

    & > div[class^="adn-"] {
      overflow: hidden;
      max-height: 370px !important;
      //height: 370px !important;
      margin: auto;
    }

    &.fipa-carousel-content__active-slider {
      display: block;
    }

    &.fipa-carousel-content__inactive-slider {
      display: none;
    }

    &.fipa-carousel-content__slide-in-left,
    &.fipa-carousel-content__slide-in-right {
      position: absolute;
      z-index: 2 !important;
      top: 0;
      display: block !important;
      animation-duration: 0.2s;
      animation-timing-function: linear;
    }

    &.fipa-carousel-content__touch_sliding {
      z-index: 1 !important;
      position: absolute !important;
      top: 0 !important;
      display: block !important;
    }
    &.fipa-carousel-content__touch_sliding_preview {
      z-index: 0;
      position: absolute;
      top: 0;
      display: block !important;
    }

    &.fipa-carousel-content__slide-in-left {
      animation-name: slide-in-left;
    }

    &.fipa-carousel-content__slide-in-right {
      animation-name: slide-in-right;
    }

    img {
      // Variable that changes in touchmove listener
      --i: 0px;
      transform: translateX(var(--i));
      will-change: transform;

      max-width: 100%;
      width: 100%;
      display: block;
      object-fit: cover;
    }
  }

  &-control {
    position: absolute;
    bottom: 30px;
    @media (max-width: 960px) {
      bottom: 15px;
    }
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    z-index: 5;
    &-radio-btn {
      border: 2px solid white !important;
      border-color: white !important;
      border-radius: 75%;
      margin-left: 0.45em;
      margin-right: 0.45em;
      cursor: pointer;
      span {
        height: 10px;
        width: 10px;
        border: 1px solid white;
        display: block;
        border-radius: inherit;
        background: white;
      }
    }
  }
}

.front-page-main-banner {
  height: 370px;
  background-color: snow;
  object-fit: cover;

  &-FI img {
    object-position: 0 bottom;
  }

  img {
    width: 100%;
    object-fit: cover;
  }
}
</style>
